<template>
  <div class="keys-list">
    <div class="filters">
      <select v-model="searchLocale" @change="debounceSearch" class="filter-select filter-locale" :title="$t('bo.translations.searchLanguage')">
        <option value="">{{ $t('bo.translations.allLanguages') }}</option>
        <option value="en">EN</option>
        <option value="fr">FR</option>
      </select>

      <input
        v-model="search"
        type="text"
        :placeholder="searchLocale ? $t('bo.translations.searchInLang', { lang: searchLocale.toUpperCase() }) : $t('bo.translations.searchPlaceholder')"
        class="search-input"
        @input="debounceSearch"
      />

      <input
        v-model="namespace"
        list="ns-filter-list"
        type="text"
        :placeholder="$t('bo.translations.allNamespaces')"
        class="filter-select"
        @input="debounceSearch"
      />
      <datalist id="ns-filter-list">
        <option v-for="ns in namespaces" :key="ns" :value="ns" />
      </datalist>

      <select v-model="missingLocale" @change="loadKeys" class="filter-select">
        <option value="">{{ $t('bo.translations.allLanguages') }}</option>
        <option value="fr">{{ $t('bo.translations.missingFR') }}</option>
      </select>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      {{ $t('bo.common.loading') }}
    </div>

    <div v-else-if="keys.length === 0" class="empty">
      {{ $t('bo.translations.noKeysFound') }}
    </div>

    <table v-else class="keys-table">
      <thead>
        <tr>
          <th>{{ $t('bo.translations.colKey') }}</th>
          <th>{{ $t('bo.translations.colNamespace') }}</th>
          <th>{{ searchLocale ? searchLocale.toUpperCase() : 'EN' }} {{ $t('bo.translations.colPreview') }}</th>
          <th>{{ $t('bo.translations.colTranslated') }}</th>
          <th>{{ $t('bo.translations.colLastUpdated') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="key in keys" :key="key.id">
          <td class="key-cell">{{ key.key }}</td>
          <td>
            <span v-if="key.namespace" class="badge">{{ key.namespace }}</span>
            <span v-else class="badge badge-empty">—</span>
          </td>
          <td class="en-preview">{{ truncate(getLocaleValue(key, searchLocale || 'en'), 50) }}</td>
          <td>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: getTranslationPercentage(key) + '%' }"
              ></div>
              <span class="progress-text">{{ getTranslationPercentage(key) }}%</span>
            </div>
          </td>
          <td class="date-cell">{{ formatDate(key.updated_at) }}</td>
          <td>
            <div class="row-actions">
              <button @click="$emit('edit', key.id)" class="btn-edit">
                ✏️ {{ $t('bo.common.edit') }}
              </button>
              <button
                @click="deleteKey(key)"
                class="btn-delete"
                :disabled="deletingId === key.id"
              >
                🗑️ {{ $t('bo.common.delete') }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pagination" v-if="pagination.last_page > 1">
      <button
        @click="goToPage(pagination.current_page - 1)"
        :disabled="pagination.current_page === 1"
        class="pagination-btn"
      >
        ← {{ $t('bo.common.previous') }}
      </button>
      <div class="pagination-info">
        {{ $t('bo.translations.pageInfo', { current: pagination.current_page, last: pagination.last_page }) }}
        <span class="pagination-total">({{ $t('bo.translations.totalKeys', { count: pagination.total }) }})</span>
      </div>
      <button
        @click="goToPage(pagination.current_page + 1)"
        :disabled="pagination.current_page === pagination.last_page"
        class="pagination-btn"
      >
        {{ $t('bo.common.next') }} →
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import translationService from '@/services/translationService'

const { t } = useI18n()

defineEmits(['edit'])

const namespaces = ref([])
const keys = ref([])
const loading = ref(false)
const search = ref('')
const searchLocale = ref('')
const namespace = ref('')
const missingLocale = ref('')
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })

const getLocaleValue = (key, locale) => {
  return key.translations?.find(t => t.locale?.code === locale)?.value || ''
}

const getTranslationPercentage = (key) => {
  if (!key.translations || key.translations.length === 0) return 0
  return Math.round((key.translations.length / 2) * 100)
}

let debounceTimer
const debounceSearch = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => loadKeys(), 300)
}

const loadKeys = async (page = 1) => {
  loading.value = true
  try {
    const response = await translationService.getKeys({
      page,
      search: search.value || undefined,
      search_locale: searchLocale.value || undefined,
      namespace: namespace.value || undefined,
      missing_locale: missingLocale.value || undefined,
    })
    keys.value = response.data
    pagination.value = {
      current_page: response.current_page,
      last_page: response.last_page,
      total: response.total,
    }
  } catch (error) {
    console.error('Load error:', error)
  } finally {
    loading.value = false
  }
}

// Suppression definitive : on montre la cle et ses valeurs, et on rappelle le risque avant d'appeler l'API.
const deletingId = ref(null)
const deleteKey = async (key) => {
  const message = t('bo.translations.confirmDelete', {
    key: key.key,
    en: getLocaleValue(key, 'en') || '—',
    fr: getLocaleValue(key, 'fr') || '—',
  })
  if (!confirm(message)) return

  deletingId.value = key.id
  try {
    await translationService.deleteKey(key.id)
    // Si la page courante est videe par la suppression, on revient a la precedente.
    const page = keys.value.length === 1 && pagination.value.current_page > 1
      ? pagination.value.current_page - 1
      : pagination.value.current_page
    await loadKeys(page)
  } catch (error) {
    console.error('Delete error:', error)
    alert(error?.response?.data?.message || t('bo.translations.deleteError'))
  } finally {
    deletingId.value = null
  }
}

const goToPage = (page) => {
  if (page >= 1 && page <= pagination.value.last_page) loadKeys(page)
}

const truncate = (text, length) => {
  if (!text) return '—'
  return text.length > length ? text.substring(0, length) + '...' : text
}

const formatDate = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  })
}

onMounted(async () => {
  try {
    namespaces.value = await translationService.getNamespaces()
  } catch (err) {
    console.error('Failed to load namespaces:', err)
  }
  loadKeys()
})

defineExpose({ loadKeys })
</script>

<style scoped>
.keys-list {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.filters {
  display: flex;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.search-input { flex: 1; min-width: 0; }

.filter-locale { width: 130px; flex-shrink: 0; font-weight: 600; }

.search-input,
.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.search-input:focus,
.filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.loading {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

.keys-table {
  width: 100%;
  border-collapse: collapse;
}

.keys-table th,
.keys-table td {
  padding: 0.875rem 1rem;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;
}

.keys-table th {
  background: #f9fafb;
  font-weight: 600;
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
}

.keys-table tbody tr:hover { background: #fafafa; }

.key-cell {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  color: #1f2937;
  font-weight: 500;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
}

.badge-empty {
  background: #f3f4f6;
  color: #9ca3af;
}

.en-preview {
  color: #6b7280;
  font-size: 0.875rem;
  max-width: 280px;
}

.progress-bar {
  position: relative;
  height: 1.5rem;
  background: #f3f4f6;
  border-radius: 0.25rem;
  overflow: hidden;
  min-width: 80px;
}

.progress-fill {
  position: absolute;
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  transition: width 0.3s ease;
}

.progress-text {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 0.7rem;
  font-weight: 600;
  color: #1f2937;
}

.date-cell {
  color: #6b7280;
  font-size: 0.8rem;
  white-space: nowrap;
}

.btn-edit {
  padding: 0.375rem 0.75rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: background 0.2s;
  white-space: nowrap;
}

.btn-edit:hover { background: #2563eb; }

.row-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-delete {
  padding: 0.375rem 0.75rem;
  background: white;
  color: #dc2626;
  border: 1px solid #fca5a5;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: background 0.2s;
  white-space: nowrap;
}

.btn-delete:hover:not(:disabled) { background: #fee2e2; }

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #3b82f6;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  color: #6b7280;
  font-size: 0.875rem;
}

.pagination-total {
  color: #9ca3af;
  margin-left: 0.25rem;
}
</style>